import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { MapPin, Users, ArrowRight, Star, Shield, Globe, Cpu, ChevronLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SEOHead, LocalBusinessSchema, BreadcrumbSchema } from '../components/SEO';
import { getCityBySlug, TIER_1_CITIES } from '../data/cities';
import { SERVICES } from '../data/services';

const CityDetail = () => {
  const { citySlug } = useParams();
  const city = getCityBySlug(citySlug);

  if (!city) return <Navigate to="/cities" replace />;

  const isTier1 = TIER_1_CITIES.some(c => c.slug === city.slug);
  const matchedServices = SERVICES.filter(s =>
    city.topServices.some(ts => ts.toLowerCase() === s.name.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white" data-testid={`city-detail-${city.slug}`}>
      <SEOHead
        title={`Human on Rent in ${city.name} - Verified Providers | HumanOnRent.AI`}
        description={`Rent verified humans in ${city.name}, ${city.state}. Available across all pin codes for events, travel, elder care & 20+ services. AI-powered matching. PIN: ${city.pins}.`}
        keywords={`human on rent ${city.name.toLowerCase()}, rent a human ${city.name.toLowerCase()}, boyfriend on rent ${city.name.toLowerCase()}, girlfriend on rent ${city.name.toLowerCase()}, hire human ${city.name.toLowerCase()}, ${city.name.toLowerCase()} human services, humanonrent ${city.name.toLowerCase()}`}
        url={`https://humanonrent.ai/cities/${city.slug}`}
      />
      <LocalBusinessSchema city={city.name} providers={city.providers} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://humanonrent.ai' },
        { name: 'Cities', url: 'https://humanonrent.ai/cities' },
        { name: city.name, url: `https://humanonrent.ai/cities/${city.slug}` }
      ]} />
      <span className="sr-only-seo">{city.seoContent}</span>
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Link to="/cities" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors mb-8 text-sm" data-testid="back-to-cities">
            <ChevronLeft className="w-4 h-4" />
            All Cities
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isTier1 ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
              {isTier1 ? 'Tier 1 Metro' : 'Tier 2 City'}
            </span>
            <span className="text-gray-500 text-sm">{city.state}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3">
            Human on Rent in <span className="gradient-text">{city.name}</span>
          </h1>
          <p className="text-lg text-gray-400 mb-8 max-w-2xl">{city.tagline}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Coverage", value: "All Pin Codes", icon: Users },
              { label: "Pin Codes", value: city.pins, icon: MapPin },
              { label: "Population", value: city.pop || "Growing", icon: Globe },
              { label: "AI-Matched", value: "Instant", icon: Cpu }
            ].map((stat, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                <stat.icon className="w-5 h-5 text-orange-400 mx-auto mb-2" />
                <p className="text-xl font-bold text-white">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About City */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">About Our Services in {city.name}</h2>
              <p className="text-gray-600 leading-relaxed mb-6">{city.desc}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {city.areas.map((area, i) => (
                  <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-sm font-medium">
                    {area}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/auth"
                  className="btn-gradient inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm"
                  data-testid="city-hire-btn"
                >
                  <Users className="w-4 h-4" /> Rent a Human in {city.name}
                </Link>
                <Link
                  to="/auth"
                  className="bg-gray-900 text-white inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm hover:bg-gray-800 transition-colors"
                  data-testid="city-provider-btn"
                >
                  Become a Provider in {city.name}
                </Link>
              </div>
            </div>

            {/* Trust Card */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Why Choose HumanOnRent.AI in {city.name}?</h3>
                <div className="space-y-4">
                  {[
                    { icon: Shield, text: "All providers are verified & background-checked" },
                    { icon: Cpu, text: "AI matches you with the best local providers" },
                    { icon: Star, text: "Rated & reviewed by real users" },
                    { icon: MapPin, text: `Covers all ${city.name} pin codes: ${city.pins}` }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-orange-600" />
                      </div>
                      <p className="text-sm text-gray-600">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Services */}
      <section className="py-16 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Top Services in {city.name}</h2>
          <p className="text-gray-500 mb-8">Most popular services booked by hirers in {city.name}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {matchedServices.map(service => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.id}
                  to={`/auth?service=${service.id}`}
                  className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-orange-200 hover:shadow-md transition-all text-center group"
                  data-testid={`city-service-${service.id}`}
                >
                  <div className={`w-12 h-12 mx-auto mb-3 ${service.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">{service.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">From Rs.1000/hr</p>
                </Link>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <Link to="/services" className="text-orange-600 font-semibold text-sm inline-flex items-center gap-1 hover:text-orange-700">
              View All 20+ Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Human on Rent in {city.name} — {city.tagline}</h2>
            <p className="text-gray-600 leading-relaxed mb-4">{city.seoContent}</p>
            <p className="text-gray-600 leading-relaxed">
              All our providers in {city.name} go through a verification process. You can view their profiles, check ratings and reviews from other hirers, 
              and book instantly through our AI-powered matching system. Services start at just Rs.1000/hr + GST. 
              Whether you need someone for a few hours or the entire day, HumanOnRent.AI in {city.name} has you covered.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 lg:px-8 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Hire in {city.name}?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Verified humans available across all pin codes in {city.name}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth" className="bg-white text-gray-900 px-8 py-3.5 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-lg" data-testid="city-cta-hire">
              Rent a Human
            </Link>
            <Link to="/auth" className="border-2 border-white text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/10 transition-all" data-testid="city-cta-provider">
              Rent Yourself & Earn
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityDetail;
